// 작성폼 제출 전 필수(required) 미입력 검증 — 순수 함수 (host 페이지가 제출 직전에 호출).
//
// 대상: 트리의 input 위젯 중 required=true이면서 사용자가 채울 수 있는 것만
//   - mode 'hidden'(입력 UI 미노출)·'readonly'(사용자가 못 채움)는 제외
//   - 현재 뷰에서 responsive 숨김(resolveWidgetHidden)이면 제외(그 뷰에선 채울 수 없음)
//   - visibleWhen 미충족(그 키의 값이 빈값 — isEmptyConditionValue, 빈 배열 포함)이면 제외(실폼 미렌더라 채울 수 없음,
//     컨테이너는 서브트리 통째. 결정: 2026-07-16-composer-combobox-dependent-value-axes P3)
//   - enabledWhen 미충족(disabled 렌더)이면 제외(보이지만 입력 불가 — visibleWhen과 동일 평가 규약)
//   - formTable 고정 행 표시 조건(row.visibleWhen, 조건식) 미충족 행의 셀 위젯도 제외(실폼 미렌더 —
//     렌더 isRowCondHidden과 같은 evaluateCondition + 같은 스코프)
//   - 반복 위젯(kind:'repeat')은 행 스코프 하강 — 행마다 children을 행 객체 스코프로 동일 규칙 검증,
//     라벨에 "N번째 행 · " prefix(결정 2026-07-20-composer-repeat-widget-row-scope §신규 투자 ②)
// 구 SMS 작성 페이지의 "사업장 담당자를 선택해주세요" 제출 차단을 데이터 기반으로 일반화.
import {
  isCompleteBusinessNumber,
  isCompleteEmail,
  isCompletePhoneNumber,
  isEmptyWriteValue,
  readBindingValue,
  toMissingRequiredField,
  walkCheckableInputs,
  type CheckableInput,
  type ComposerWidget,
  type InputWidget,
  type MissingRequiredField,
  type MissingRequirement,
} from '@safetyhub/shared';
import { resolveWidgetHidden } from './composerWidgetTreeRender';

// isEmptyWriteValue(미입력 판정)·MissingRequiredField·순회 골격은 서버 완료 검증과 공유하기 위해
// `@safetyhub/shared`(composer-write-validation, 순수 모듈)로 이관(2026-08-24) — 기존 import 경로
// 유지를 위한 재-export(evaluateCondition 이관과 같은 규약).
export { isEmptyWriteValue };
export type { MissingRequiredField, MissingRequirement };

/**
 * 사용자가 채울 수 있는 input형 노드만 방문하는 공통 순회 — 필수(collectMissingRequired)와
 * 입력 형식(collectInvalidInputFormat) 검증이 같은 제외 규칙(hidden/readonly, responsive 숨김,
 * visibleWhen/enabledWhen 미충족)을 공유하는 단일 진실. 표 고정 행 셀 위젯 하강 포함.
 *
 * 반복 위젯(kind:'repeat')은 행 스코프로 하강한다(P3 — 결정 2026-07-20-composer-repeat-widget-row-scope
 * §신규 투자 ②): values[over] 행 배열을 순회하며 행마다 children을 그 행 객체(rowValues) 스코프로
 * 검증 — 값 읽기·visibleWhen/enabledWhen 평가 모두 행 스코프(렌더 RepeatRowsBlock의
 * rowValues 주입과 동일 경계). 행 배열 아님/빈 배열은 안전 통과(행 자체의 필수성은 범위 밖).
 * 방문 시 labelPrefix("N번째 행 · ", 중첩 repeat는 누적)로 행 식별을 라벨에 동반한다.
 * formTable의 반복 행(row.type==='repeat', cellFields 계보)·modalTrigger children(모달 스코프 —
 * 확인 시점 검증은 후속)은 기존대로 제외.
 */
function walkUserEditableInputs(
  tree: ComposerWidget[],
  values: Record<string, unknown>,
  view: string | undefined,
  visit: (w: CheckableInput, scope: Record<string, unknown>, labelPrefix: string) => void,
): void {
  // 하강·제외 규칙(mode/visibleWhen/enabledWhen, repeat 행 스코프, branch 활성 슬롯,
  // formTable 고정 행 조건)은 shared walkCheckableInputs가 단일 정본(서버 완료 검증과 공유) —
  // rc 고유 축인 responsive 현재 뷰 숨김(resolveWidgetHidden)만 여기서 주입한다.
  walkCheckableInputs(tree, values, visit, {
    isNodeHidden: (w) => resolveWidgetHidden(w, view),
  });
}

/** composerWidgetTree 재귀로 필수 미입력 위젯 수집(트리 순서 = 화면 순서). */
export function collectMissingRequired(
  tree: ComposerWidget[],
  values: Record<string, unknown>,
  view?: string,
): MissingRequiredField[] {
  const out: MissingRequiredField[] = [];
  // 표 고정 행 셀 위젯(중첩 InputWidget)의 필수 검증 — input과 동일 규칙(hidden/readonly 제외).
  // 반복 위젯(kind:'repeat')은 행 스코프 하강(값 읽기=행 객체, 라벨="N번째 행 · " prefix) —
  // bindingKey는 행 상대키 그대로(반환 계약 무변). formTable 반복 행(cellFields)은 기존대로 제외.
  walkUserEditableInputs(tree, values, view, (w, scope, labelPrefix) => {
    // required 판정 + readBindingValue 읽기(점 경로 중첩 폴백 — 2026-08-24 DLR 실사고) + 형상
    // 생성은 shared toMissingRequiredField가 세 소비처(서버 완료 검증 포함) 단일 정본.
    const missing = toMissingRequiredField(w, scope, labelPrefix);
    if (missing) out.push(missing);
  });
  return out;
}

/** inputFormat별 완결 판정 + 안내 예시 — collectInvalidInputFormat의 단일 진실(축 확장 시 여기만 추가). */
const INPUT_FORMAT_RULES: Record<
  NonNullable<InputWidget['inputFormat']>,
  { isComplete: (v: string) => boolean; example: string }
> = {
  phone: { isComplete: isCompletePhoneNumber, example: '010-1234-5678' },
  'business-number': { isComplete: isCompleteBusinessNumber, example: '123-45-67890' },
  email: { isComplete: isCompleteEmail, example: 'name@example.com' },
};

/** 형식 미완성 항목 — 안내 문구 조립용 형식별 예시 동반. */
export interface InvalidInputFormatField extends MissingRequiredField {
  /** 완결 형식 예시(예: '010-1234-5678'). */
  example: string;
}

/**
 * inputFormat 부여 위젯 중 값이 비어 있지 않은데 완결 패턴(INPUT_FORMAT_RULES — phone은 유선 02
 * 포함) 미충족인 것 수집 — submitAction 모달 확인(등록) 게이트 전용.
 * 빈 값은 대상 아님(필수 여부는 required 축/서버가 담당) — 일반 작성폼 제출은 이 검증을
 * 걸지 않는다(형식 미완성이어도 값 저장은 허용, 등록 경로만 게이트 — 2026-07-18).
 */
export function collectInvalidInputFormat(
  tree: ComposerWidget[],
  values: Record<string, unknown>,
  view?: string,
): InvalidInputFormatField[] {
  const out: InvalidInputFormatField[] = [];
  walkUserEditableInputs(tree, values, view, (w, scope, labelPrefix) => {
    const rule = w.inputFormat ? INPUT_FORMAT_RULES[w.inputFormat] : undefined;
    if (!rule) return;
    // required 검증과 같은 readBindingValue 규약(점 경로 bindingKey의 중첩 값 도달).
    const v = readBindingValue(scope, w.bindingKey);
    if (typeof v !== 'string' || v.trim() === '') return;
    if (!rule.isComplete(v.trim())) {
      out.push({
        id: w.id,
        bindingKey: w.bindingKey,
        label: labelPrefix + (w.label ?? w.bindingKey),
        // 형식 축은 값이 «있는데» 미완결인 것이라 대상은 언제나 값 칸이다(행 축은 성립하지 않는다).
        requirement: 'value',
        example: rule.example,
      });
    }
  });
  return out;
}

// ⚠️ 2026-09-30(S3-b): 파생 입력 위젯 전용이던 `collectMissingRequiredNodes`는 **삭제됐다**.
//   flat 렌더 갈래가 사라져 파생 산출(`deriveWriteForm`의 `InputWidget[]`)도 저작 트리와 같은
//   `collectMissingRequired`가 검증한다 — `InputWidget`이 `ComposerWidget` union 멤버라 평평한
//   root 배열을 그대로 넘길 수 있고, 파생 노드는 `visibleWhen`·`enabledWhen`·responsive 숨김 축을
//   갖지 않으므로(writeForm.ts에 0건) 추가 제외 규칙이 발동하지 않아 판정이 같다.
//   되살리지 말 것: 검증 갈래가 둘이면 렌더가 보는 트리와 검증이 보는 배열이 갈린다(실제로 세
//   호스트 전부에서 갈려 있었다 — web·admin은 `effectiveTree`가 아니라 raw 트리를 검증했다).
//   파생 형상의 불변식은 `__tests__/requiredValidation.test.ts`의 「파생 형상(평평한 root 배열)」
//   describe가 같은 축으로 계속 잰다.
